import React from "react";
import type { DirectGrossMarginTrendItem } from "../type";
import { colors } from "../../../utils/colors";
import { useApp } from "../../../contexts/AppContext";
import { GenericBarChart, GenericBarChartRow } from "./GenericBarChart";
import {
  DIRECT_GROSS_MARGIN_SERIES_NAME,
  EMPTY_DIRECT_GROSS_MARGIN_STATE_MESSAGE,
} from "../../../utils/consts";

const monthShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toChartRows = (series: DirectGrossMarginTrendItem[]) =>
  series.map((p) => ({
    label: `${monthShort[p.month - 1] ?? p.month} ${p.year}`,
    value: Number(p.margin_pct),
  }));

const formatDgmCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

export interface DirectGrossMarginTrendChartProps {
  title?: string;
  loading?: boolean;
  series: DirectGrossMarginTrendItem[];
}

export const DirectGrossMarginTrendChart: React.FC<
  DirectGrossMarginTrendChartProps
> = ({ title, loading = false, series }) => {
  const { drawerOpen } = useApp();

  const rows: GenericBarChartRow[] = toChartRows(series);

  return (
    <GenericBarChart
      title={title}
      data={rows}
      seriesName={DIRECT_GROSS_MARGIN_SERIES_NAME}
      loading={loading}
      height={400}
      minHeight={400}
      emptyMessage={EMPTY_DIRECT_GROSS_MARGIN_STATE_MESSAGE}
      legendTop={8}
      legendRight={50}
      barWidth="35%"
      xAxisLabelColor={colors.black}
      xAxisLabelFontSize="0.625rem"
      xAxisLabelFontWeight={500}
      yAxisLabelColor={colors.chart_gray}
      yAxisLabelFontSize="0.6875rem"
      xAxisRotateThreshold={5}
      defaultBarColor={colors.bar_green}
      yAxisValueFormatter={(val: number) => `${Number(val).toFixed(1)}%`}
      axisTooltipFormatter={({
        dataIndex,
        categoryLabel,
        primaryValue,
        seriesMarkerHtml,
      }) => {
        const point = series[dataIndex];
        const pct = `${primaryValue.toFixed(2)}%`;
        const actual = point ? formatDgmCurrency(point.dgm_value) : "N/A";
        return `<div style="color:${colors.chart_text};">${categoryLabel}<br/><span style="display:inline-block;margin-top:6px;">${seriesMarkerHtml}${DIRECT_GROSS_MARGIN_SERIES_NAME} <span style="display:inline-block;padding-left:14px;"><strong>${pct}</strong> (<strong>${actual}</strong>)</span></span></div>`;
      }}
      resizeTrigger={drawerOpen}
    />
  );
};
